<template>
  <span
    v-if="titleConfig"
    class="user-title"
    :class="sizeClass"
    :style="titleStyle"
  >
    {{ titleText }}
  </span>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  titleConfig: {
    type: [Object, String],
    default: null
  },
  /** 紧凑（排行榜/资料卡）、默认、商城大号 */
  size: {
    type: String,
    default: 'default',
    validator: (v) => ['compact', 'default', 'shop'].includes(v)
  }
})
const sizeClass = computed(() => `user-title--${props.size}`)

// 解析称号文本
const titleText = computed(() => {
  if (!props.titleConfig) return ''
  
  // 如果 titleConfig 是字符串，直接返回
  if (typeof props.titleConfig === 'string') {
    try {
      // 尝试解析 JSON
      const parsed = JSON.parse(props.titleConfig)
      return parsed.text || parsed.name || props.titleConfig
    } catch {
      // 不是 JSON，直接返回
      return props.titleConfig
    }
  }
  
  // 如果是对象，获取文本
  if (typeof props.titleConfig === 'object' && props.titleConfig !== null) {
    return props.titleConfig.text || props.titleConfig.name || ''
  }
  
  return ''
})

// 称号样式
const titleStyle = computed(() => {
  if (!props.titleConfig) return {}
  
  let config = props.titleConfig
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config)
    } catch {
      return {}
    }
  }
  
  if (typeof config !== 'object' || config === null) {
    return {}
  }
  
  const style = {}
  
  // 文字颜色
  if (config.textColor) {
    style.color = config.textColor
  }
  
  // 字体样式
  if (config.fontWeight) {
    style.fontWeight = config.fontWeight
  }
  if (config.fontStyle) {
    style.fontStyle = config.fontStyle
  }
  if (config.fontSize) {
    style.fontSize = config.fontSize
  }
  
  // 背景样式
  if (config.backgroundColor) {
    style.backgroundColor = config.backgroundColor
  }
  if (config.backgroundImage) {
    style.backgroundImage = config.backgroundImage
  }
  if (config.backgroundSize) {
    style.backgroundSize = config.backgroundSize
  }
  if (config.backgroundPosition) {
    style.backgroundPosition = config.backgroundPosition
  }
  if (config.backgroundRepeat) {
    style.backgroundRepeat = config.backgroundRepeat
  }
  if (config.borderColor) {
    style.borderColor = config.borderColor
    style.borderWidth = config.borderWidth || '1px'
    style.borderStyle = 'solid'
  }
  if (config.borderRadius) {
    style.borderRadius = config.borderRadius
  }
  if (config.padding) {
    style.padding = config.padding
  }
  
  // 特效
  if (config.textShadow) {
    style.textShadow = config.textShadow
  }
  if (config.boxShadow) {
    style.boxShadow = config.boxShadow
  }
  if (config.glow) {
    const radius = config.glow.radius || '10px'
    const blur = clampGlowRadius(radius, 8)
    style.textShadow = `0 0 ${blur} ${config.glow.color || 'rgba(255,255,255,0.4)'}`
  }
  return style
})

function clampGlowRadius(radius, maxPx) {
  const num = parseInt(String(radius).replace(/px|em/g, ''), 10)
  if (isNaN(num)) return `${maxPx}px`
  return `${Math.min(num, maxPx)}px`
}
</script>

<style scoped lang="scss">
.user-title {
  display: inline-block;
  padding: 2px 8px;
  border-radius: var(--cosmetic-title-radius-default, 6px);
  font-size: var(--cosmetic-title-font-size, 12px);
  font-weight: 500;
  white-space: nowrap;
  max-width: min(var(--cosmetic-title-max-width, 200px), 100%);
  width: auto;
  box-sizing: border-box;
  min-height: var(--cosmetic-title-height, 28px);
  line-height: 1.25;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;

  &--compact {
    font-size: var(--cosmetic-title-font-size, 12px);
    min-height: var(--cosmetic-title-height-compact, 24px);
    border-radius: var(--cosmetic-title-radius-basic, 4px);
  }

  &--default {
    border-radius: var(--cosmetic-title-radius-default, 6px);
  }

  &--shop {
    font-size: var(--cosmetic-title-font-size-shop, 16px);
    min-height: var(--cosmetic-title-height-shop, 32px);
    border-radius: var(--cosmetic-title-radius-default, 6px);
  }
}
</style>
